import { Component, Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(
    private readonly snackBar: MatSnackBar,
    public dialog: MatDialog
  ) { }

  /**
   * Presents a toast displaying the message with a green background
   * @param message Message to display
   * @example
   * this.notificationService.success("confirm oked");
   */
  success(message: string,action:string='') {
    this.openSnackBar(message, action, 'success-snackbar');
  }

  /**
   * Presents a toast displaying the message with a red background
   * @param message Message to display
   * @example
   * this.notificationService.error("confirm canceled");
   */
  error(message: string,action:string='') {
    this.openSnackBar(message, action, 'error-snackbar');
  }
  info(message: string,action:string='') {
    this.openSnackBar(message, '', 'blue-snackbar');
  }

 
  /**
   * Shows a confirmation modal, presenting the user with
   * an OK and Cancel button. 
   * @param message Body of the modal
   * @param okCallback Optional function to call when the user clicks Ok
   * @param title Optional modal title
   * @param cancelCallback Option function to call when the user clicks Cancel
   * @example
   * //displays a success or error message depending on what button is clicked.
   * this.notificationService.confirmation(
   * 'it will be gone forever', //message body
   * () => { //okCallback
      this.notificationService.success("confirm oked");
    },
    'Are you sure?', //title
     () => { //cancelCallback
      this.notificationService.error("confirm canceled");
    });
   */
  confirmation(
    message: string,
    displayYesNo:string="Y",
    buttonText:string="Ok",
    okCallback: () => void,
    title = 'Are you sure?',
    cancelCallback: () => any = () => { }
  ) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      width: '450px',
      disableClose: true,
      data: { message: message, title: title,displayYesNo:displayYesNo,buttonText:buttonText },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && okCallback) {
        okCallback();
      }
      if (!result && cancelCallback) {
        cancelCallback();
      }
    });
  }

  /**
  * Shows a modal, presenting the user with an OK button.
  * @param message Body of the modal
  * @param okCallback Optional function to call when the user clicks Ok
  * @param title Optional modal title
  * @example
  * //displays a success when the Ok button is clicked.
  *  this.notificationService.alert("an alert", "notice", () => {
      this.notificationService.success("alert oked");
    });
  */
  alert(message: string, title = 'Notice', okCallback: () => void = () => { }) {
    const dialogRef = this.dialog.open(AlertDialog, {
      width: '450px',
      data: { message: message, title: title },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && okCallback) {
        okCallback();
      }
    });
  }


  /**
   * Displays a toast with provided message
   * @param message Message to display
   * @param action Action text, e.g. Close, Done, etc
   * @param className Optional extra css class to apply
   * @param duration Optional number of SECONDS to display the notification for
   */
  openSnackBar(
    message: string,
    action: string,
    className = '',
    duration = 10000
  ) {
    this.snackBar.open(message, action, {
      duration: duration,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: [className]
    });
  }
}

export interface DialogData {
  actiontype: any;
  editdata: any;
  name: any;
  message: string;
  title: string;
  buttonText:string;
  displayYesNo:string;
}

@Component({
  template: `

<h2 mat-dialog-title style="padding: 10px; font-family: 'OpenSans-Regular', 'Helvetica Neue', Arial, Helvetica, sans-serif !important;" >{{ data.title }}</h2>
    <div mat-dialog-content style=" padding: 10px; font-family: 'OpenSans-Regular', 'Helvetica Neue', Arial, Helvetica, sans-serif !important;" >
     <h4>{{data.message}}</h4>
    </div>
    <br>
    @if(data.displayYesNo=='Y'){
      <div mat-dialog-actions style="padding: 10px; ">
       <button mat-raised-button style="float: right; margin-right: 10px; color: #fff; background-color: #3f51b5; border-radius: 4px; border: none; cursor: pointer; font-family: 'OpenSans-Regular', 'Helvetica Neue', Arial, Helvetica, sans-serif !important;  min-width: 64px; line-height: 36px; padding: 0 16px; font-weight: 500;"
        (click)="onNoClick()">
        No
      </button>
      <button mat-raised-button style="float: right; margin-right: 10px; color: #fff; background-color: green;  border-radius: 4px; border: none; cursor: pointer; font-family: 'OpenSans-Regular', 'Helvetica Neue', Arial, Helvetica, sans-serif !important; min-width: 64px; line-height: 36px; padding: 0 16px; font-weight: 500;"
       (click)="onYesClick()" cdkFocusInitial>
        Yes
      </button>
    </div>
    }
    @else{
    <div mat-dialog-actions style="padding: 10px; " >
    <button mat-raised-button style="float: right; margin-right: 10px; color: #fff; background-color:#3f51b5;  border-radius: 4px; border: none; cursor: pointer; font-family: 'OpenSans-Regular', 'Helvetica Neue', Arial, Helvetica, sans-serif !important; min-width: 64px; line-height: 36px; padding: 0 16px; font-weight: 500;"
       (click)="onYesClick()" cdkFocusInitial>
        {{data.buttonText}}
      </button>
    </div>
    }
  `
})
export class ConfirmationDialog {
  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) { }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onYesClick(): void {
    this.dialogRef.close(true);
  }
}

@Component({
  template: `
    <h1 mat-dialog-title>{{ data.title }}</h1>
    <div mat-dialog-content>
     {{data.message}}
    </div>
    <div mat-dialog-actions>
      <button mat-raised-button color="primary" (click)="onYesClick()" cdkFocusInitial>
        Ok
      </button>
    </div>
  `
})
export class AlertDialog {
  constructor(
    public dialogRef: MatDialogRef<AlertDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) { }

  onYesClick(): void {
    this.dialogRef.close(true);
  }
}
